import * as React from "react";
import {
  Delete,
  Edit,
  Search,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormLabel,
  Grid,
  IconButton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import AddLocationIcon from "@mui/icons-material/AddLocation";
import { useEffect, useState, useMemo } from "react";
import { Close } from "@mui/icons-material";
import AssignLocation from "./assignLocation";
import AddEditDialog from "./addLocationDialog";
import { toast } from "react-toastify";
import { axiosInstance } from "../../../utils/tokenSetCookie";
import { LocationClass } from "../../../types/types";
import { MaterialReactTable, type MRT_ColumnDef } from "material-react-table";
import LoginRestriction from "../../../utils/loginRestriction";
import { AxiosError } from "axios";
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

const ManageLocationsClass = () => {
  const { t } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [_locationToDelete, setLocationToDelete] = useState<number | null>(
    null
  ); // ID of the location to delete
  const [_formData, setFormData] = useState({ name: "", status: "on" });
  const [dialogMode, setDialogMode] = useState("add"); // "add" or "edit"
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
  const [locationsclass, setLocationsClass] = useState<LocationClass[]>([]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);

  //API State
  const [isLocationClassName, setLocationClassName] = useState("");
  const [isSearchLocationClassName, setSearchLocationClassName] = useState("");
  const [isEditLocationId, setEditLocationId] = useState<number | null>(null);
  const [deletedList, setDeletedList] = useState<boolean>(false);
  const [isEditClassId, setEditClassId] = useState<{
    id: number;
    className: string;
  }>({ id: 0, className: "" });

  const [isCallbackDialogOpen, setIsCallbackDialogOpen] = useState(false);
  const [isRestrictionOpen, setIsRestrictionOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Simulate a 3-second loading period
  useEffect(() => {
    if (loading === true) {
      const timer = setTimeout(() => {
        setLoading(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [loading]);

  useEffect(() => {
    const getLocationClassDetail = async () => {
      try {
        const response = await axiosInstance.get(
          API_BASE_URL + "locationclass/list"
        );
        if (response.status === 200) {
          setLocationsClass(response.data.data); // Save data to state
        }
      } catch (error) {
        const errCode = error as AxiosError<{ status: number }>;
        if (errCode.response && errCode.response.status === 401) {
          setIsRestrictionOpen(true);
        } else {
          toast.error("Something went wrong", {
            position: "top-right",
            autoClose: 1000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: false,
            draggable: false,
            progress: undefined,
          });
        }
        throw error;
      }
    };
    getLocationClassDetail(); // Passing the payload as an argument
  }, []);

  const getLocationClass = () => {
    const getLocationDetail = async () => {
      try {
        const response = await axiosInstance.get(
          API_BASE_URL + "locationclass/list"
        );
        if (response.status === 200) {
          setLocationsClass(response.data.data); // Save data to state
        }
      } catch (error) {
        const errCode = error as AxiosError<{ status: number }>;
        if (errCode.response && errCode.response.status === 401) {
          setIsRestrictionOpen(true);
        } else {
          toast.error("Something went wrong", {
            position: "top-right",
            autoClose: 1000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: false,
            draggable: false,
            progress: undefined,
          });
        }
        throw error;
      }
    };
    getLocationDetail();
  };

  // Add location class dialog
  const handleOpenAddDialog = () => {
    setDialogMode("add");
    setIsDialogOpen(true);
    setLocationClassName("");
    setFormData({ name: "", status: "on" });
  };

  // Edit Location Class dialog
  const handleOpenEditDialog = (id: number, className: string) => {
    setDialogMode("update");
    setIsDialogOpen(true);
    setEditLocationId(id);
    setLocationClassName(className);
    // editLocationClass(id);
  };

  // Edit Location Class function
  // const editLocationClass = (id: number) => {};
  const handledeleteDialog = (id: number, className: string) => {
    setDialogMode("delete");
    setEditLocationId(id);
    setLocationToDelete(id);
    setLocationClassName(className);
    setIsDeleteDialogOpen(true);
  };

  const handleSave = async () => {
    if (!isLocationClassName.trim()) {
      toast.error(t("please_enter_location_class_name"));
      return;
    }
    const payload =
      dialogMode === "add"
        ? { webSiteId: 1, className: isLocationClassName }
        : {
            id: isEditLocationId,
            type: dialogMode,
            className: isLocationClassName,
          };

    try {
      const endPoint =
        dialogMode === "add"
          ? axiosInstance.post(API_BASE_URL + "locationclass/addclass", payload)
          : axiosInstance.put(
              API_BASE_URL + "locationclass/updateclass",
              payload
            );

      const response = await endPoint;

      if (response.status >= 200 && response.status <= 300) {
        getLocationClass();
        toast.success(
          dialogMode === "add"
            ? t("location_class_added_successfully")
            : t("location_class_updated_successfully"),
          {
            position: "top-right",
            autoClose: 1000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          }
        );
        setIsDialogOpen(false);
        setIsDeleteDialogOpen(false);
        setLocationClassName("");
      }
    } catch (error) {
      const errCode = error as AxiosError<{ status: number }>;
      if (errCode.response && errCode.response.status === 401) {
        setIsRestrictionOpen(true);
      } else {
        toast.error(
          dialogMode === "add"
            ? t("failed_to_add_location_class")
            : t("failed_to_update_location_class"),
          {
            position: "top-right",
            autoClose: 1000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: false,
            draggable: false,
            progress: undefined,
          }
        );
      }
      throw error;
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setIsDeleteDialogOpen(false);
    setLocationToDelete(null); // Clear the location ID
    setFormData({ name: "", status: "on" });
  };

  const handleSearch = async () => {
    // Conditions based on search location details
    const endPoint =
      deletedList === true
        ? `?deletedOn=${deletedList}&className=${isSearchLocationClassName}`
        : `?className=${isSearchLocationClassName}`;
    try {
      const response = await axiosInstance.get(
        API_BASE_URL + `locationclass/list${endPoint}`
      );
      if (response.status >= 200 && response.status <= 300) {
        setLocationsClass(response.data.data);
      }
    } catch (error) {
      const errCode = error as AxiosError<{ status: number }>;
      if (errCode.response && errCode.response.status === 401) {
        setIsRestrictionOpen(true);
      } else {
        toast.error("Something went wrong", {
          position: "top-right",
          autoClose: 1000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: false,
          draggable: false,
          progress: undefined,
        });
      }
      throw error;
    }
  };


  const handleOpenAssignDialog = async (id: number, className: string) => {
    setEditClassId({ id: id, className: className });
    setIsAssignDialogOpen(true);
  };
  const handleChange = async (
    _event: React.MouseEvent<HTMLElement>,
    newAlignment: boolean | null
  ) => {
    if (newAlignment !== null) {
      setDeletedList(newAlignment);

      // Use the new value directly
      const endPoint = newAlignment
        ? `?deletedOn=${newAlignment}&className=${isSearchLocationClassName}`
        : `?className=${isSearchLocationClassName}`;

      try {
        const response = await axiosInstance.get(
          API_BASE_URL + `locationclass/list${endPoint}`
        );
        if (response.status >= 200 && response.status <= 300) {
          setLocationsClass(response.data.data);
        }
      } catch (error) {
        const errCode = error as AxiosError<{ status: number }>;
        if (errCode.response && errCode.response.status === 401) {
          setIsRestrictionOpen(true);
        } else {
          toast.error("Something went wrong", {
            position: "top-right",
            autoClose: 1000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: false,
            draggable: false,
            progress: undefined,
          });
        }
      }
    }
  };

  const handleCloseAssignDialog = () => {
    setIsAssignDialogOpen(false);
  };

  const columns = useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "className",
        header: t("location_class_name"),
        size: 200,
      },
      {
        id: "actions",
        header: t("actions"),
        Cell: ({ row }) => (
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <IconButton
              color="primary"
              sx={{ padding: 0 }}
              onClick={() =>
                handleOpenEditDialog(row.original.id, row.original.className)
              }
            >
              <Edit />
            </IconButton>
            <IconButton
              className="deleteicon"
              sx={{ padding: 0 }}
              onClick={() =>
                handledeleteDialog(row.original.id, row.original.className)
              }
            >
              <Delete />
            </IconButton>
            <Button
              className="addbtn"
              onClick={() =>
                handleOpenAssignDialog(row.original.id, row.original.className)
              }
              variant="contained"
              color="primary"
              sx={{
                textTransform: "none",
              }}
            >
              {t("assign_location")}
            </Button>
          </Box>
        ),
      },
    ],
    [t, handleOpenEditDialog, handledeleteDialog, handleOpenAssignDialog]
  );

  const handleCloseCallbackDialog = () => {
    setIsCallbackDialogOpen(false);
  };

  const handleRequestCallback = () => {
    // Add your callback request logic here
    toast.success(t("callback_request_submitted"));
    setIsCallbackDialogOpen(false);
  };

  return (
    <>
      <Box>
        {/* First section - Search Form */}
        <Box
          sx={{
            backgroundColor: "#fff",
            borderRadius: "12px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            margin: {
              xs: "10px 8px",
              sm: "15px 15px",
              md: "20px 20px",
            },
            overflow: "hidden",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              borderBottom: "1px solid #eee",
              padding: "20px 24px",
              fontWeight: 600,
              color: "primary.main",
            }}
          >
            {t("manage_location_classes")}
          </Typography>

          <form>
            <Grid
              container
              spacing={3}
              sx={{
                padding: { xs: 2, sm: 3 },
                "& .MuiFormLabel-root": {
                  marginBottom: 1,
                  color: "text.secondary",
                  fontWeight: 500,
                },
              }}
            >
              <Grid item xs={12} md={6}>
                <FormLabel>{t("location_class_name")}</FormLabel>
                <TextField
                  value={isSearchLocationClassName}
                  onChange={(e) => setSearchLocationClassName(e.target.value)}
                  size="small"
                  variant="outlined"
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6} sx={{ mt: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 2,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <ToggleButtonGroup
                      color="primary"
                      value={deletedList}
                      exclusive
                      onChange={handleChange}
                      size="small"
                      sx={{
                        "& .MuiToggleButton-root": {
                          borderRadius: "6px",
                          px: 3,
                          "&.Mui-selected": {
                            backgroundColor: "primary.main",
                            color: "white",
                          },
                        },
                      }}
                    >
                      <ToggleButton value={true}>{t("on")}</ToggleButton>
                      <ToggleButton value={false}>{t("off")}</ToggleButton>
                    </ToggleButtonGroup>
                    <Typography sx={{ color: "text.secondary" }}>
                      {t("view_deleted")}
                    </Typography>
                  </Box>

                  <Button
                    variant="contained"
                    startIcon={<Search />}
                    onClick={handleSearch}
                    sx={{
                      textTransform: "none",
                      borderRadius: "8px",
                      px: 3,
                    }}
                  >
                    {t("search")}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Box>

        {/* Second section - Table */}
        <Box
          sx={{
            backgroundColor: "#fff",
            borderRadius: "12px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            margin: {
              xs: "10px 8px",
              sm: "15px 15px",
              md: "20px 20px",
            },
            padding: { xs: 1.5, sm: 2 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mb: 3,
            }}
          >
            <Button
              variant="contained"
              startIcon={<AddLocationIcon />}
              onClick={handleOpenAddDialog}
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                px: 3,
              }}
            >
              {t("location_class")}
            </Button>
          </Box>

          <MaterialReactTable
            columns={columns}
            data={locationsclass}
            enableDensityToggle={true}
            enableHiding={true}
            enableColumnFilters={true}
            enableGlobalFilter={true}
            enableFullScreenToggle={true}
            enableColumnActions={true}
            initialState={{
              showColumnFilters: false,
              showGlobalFilter: true,
              pagination: {
                pageSize: rowsPerPage,
                pageIndex: page,
              },
            }}
            positionGlobalFilter="left"
            renderTopToolbarCustomActions={() => (
              <Box sx={{ display: "flex", gap: 2, p: 1 }}>
                {/* Add any custom buttons here if needed */}
              </Box>
            )}
            muiSearchTextFieldProps={{
              placeholder: t("search_location_classes"),
              sx: { minWidth: "300px" },
              variant: "outlined",
              size: "small",
            }}
            muiTableHeadCellProps={{
              sx: {
                backgroundColor: "#f5f5f5",
                fontWeight: 600,
              },
            }}
            muiTableBodyRowProps={{
              hover: true,
            }}
            muiTablePaperProps={{
              sx: {
                "& .MuiTable-root": {
                  "& .MuiTableBody-root": {
                    "& .MuiTableRow-root": {
                      "&:nth-of-type(odd)": {
                        backgroundColor: "white",
                      },
                      "&:nth-of-type(even)": {
                        backgroundColor: "#f5f5f5",
                      },
                    },
                  },
                },
              },
            }}
            onPaginationChange={(updater) => {
              const newPagination =
                typeof updater === "function"
                  ? updater({ pageIndex: page, pageSize: rowsPerPage })
                  : updater;
              setPage(newPagination.pageIndex);
              setRowsPerPage(newPagination.pageSize);
            }}
            rowCount={locationsclass.length}
          />

          <AssignLocation
            open={isAssignDialogOpen}
            onClose={handleCloseAssignDialog}
            editId={isEditClassId}
          />

          <AddEditDialog
            open={isDialogOpen}
            onClose={handleCloseDialog}
            mode={dialogMode}
            editLocationId={isEditLocationId}
            editLocationName={isLocationClassName}
            setLocationName={setLocationClassName}
            onSave={handleSave}
          />

          {/* Delete Confirmation Dialog */}
          <Dialog
            open={isDeleteDialogOpen}
            onClose={handleCloseDialog}
            sx={{
              "& .MuiDialog-paper": {
                width: { xs: "90%", sm: "600px" }, // Responsive dialog width
                maxWidth: "100%",
              },
            }}
          >
            <DialogTitle className="dialogtitle">
              <Typography>{t("are_you_sure")}</Typography>
              <IconButton
                edge="end"
                color="inherit"
                onClick={handleCloseDialog}
                aria-label="close"
                sx={{
                  margin: "auto",
                  position: "absolute",
                  right: "25px",
                  top: "17px",
                  border: "1px solid #be1e2d", // Red border
                  color: "#be1e2d",
                  width: "30px",
                  height: "30px",
                  borderRadius: "50%",
                  "&:hover": {
                    backgroundColor: "#be1e2d", // Full red background on hover
                    color: "#ffffff", // White icon color
                  },
                }}
              >
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ marginTop: 2 }}>
              <Typography>
                {t("total_associated_locations")}: 0<br />
                {t("total_associated_users")}: 0
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog} className="cancelbtn">
                {t("no")}
              </Button>
              <Button
                color="primary"
                variant="contained"
                onClick={handleSave} // Call the delete function
                className="addbtn"
              >
                {t("yes")}{" "}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={isCallbackDialogOpen}
            onClose={handleCloseCallbackDialog}
            sx={{
              "& .MuiDialog-paper": {
                width: { xs: "90%", sm: "500px" },
                maxWidth: "100%",
                borderRadius: "12px",
              },
            }}
          >
            <DialogTitle
              sx={{
                pb: 1,
                borderBottom: "1px solid #eee",
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t("add_multiple_clinics")}
              </Typography>
              <IconButton
                edge="end"
                color="inherit"
                onClick={handleCloseCallbackDialog}
                aria-label="close"
                sx={{
                  position: "absolute",
                  right: "25px",
                  top: "17px",
                  border: "1px solid #be1e2d",
                  color: "#be1e2d",
                  width: "30px",
                  height: "30px",
                  borderRadius: "50%",
                  "&:hover": {
                    backgroundColor: "#be1e2d",
                    color: "#ffffff",
                  },
                }}
              >
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
              <Typography>
                <br />
                {t(
                  "To add multiple clinics under this account, please request a callback, and our team will reach out to you within 24 hours."
                )}
              </Typography>
            </DialogContent>
            <DialogActions sx={{ p: 2.5, gap: 1 }}>
              <Button
                onClick={handleCloseCallbackDialog}
                variant="outlined"
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                  minWidth: "100px",
                }}
              >
                {t("cancel")}
              </Button>
              <Button
                onClick={handleRequestCallback}
                variant="contained"
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                  minWidth: "150px",
                }}
              >
                {t("request_callback")}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Box>
      <LoginRestriction open={isRestrictionOpen} />
    </>
  );
};

export default ManageLocationsClass;
